import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
    OrdersToFulfillPageQuery,
    RoleId,
    useCurrentUserQuery,
    useOrdersToFulfillPageQuery,
    useUpdateOrderRequestPrioritiesMutation,
} from '../../../../services';
import { isInventoryTracked } from '../../../../services/entities';
import { DPage, DPageBody, DPageLoader } from '../../../dum';
import { SUpdateOrderRequestStatusDialog } from '../../../smart';
import { PlanningOrderCard, PlanningMove } from './components/planning-order-card';
import { CoverageLegend } from './components/coverage-legend';

type RequestProductRow = NonNullable<
    OrdersToFulfillPageQuery['getOptimizedRequestProducts']
>[number];

export type PlanningCoverage = 'full' | 'partial' | 'none';

export type PlanningItem = {
    product_id: number | null;
    product_description: string;
    // a request line is expressed in bultos when it has groups; otherwise kilos
    unit: 'bultos' | 'kg';
    requested: number;
    // Raw product inventory as the warehouse reports it, before this board's
    // queue consumes anything. Shown only in the tooltip, so the planner can see
    // where `available` came from.
    inventory: number;
    // Stock promised to sales that are not yet Entregado and that NO line on
    // this board will draw down — a direct sale with no pedido, or a pedido that
    // has left status 1/2. It is gone from the warehouse in practice, so it is
    // removed from the pool; surfaced so the shortfall is explainable rather
    // than looking like the numbers drifted.
    committed: number;
    remaining: number;
    // Portion of `remaining` this line's turn in the queue covers from stock
    // (min(remaining, max(0, available))). Subtracted from `remaining` to show
    // what is still uncovered after the inventory allocation.
    covered: number;
    // Stock still unclaimed by earlier tickets when this line was allocated.
    // MAY BE NEGATIVE: a product can owe more than it holds, and clamping that
    // to 0 is what the raw-inventory display was working around.
    available: number;
    coverage: PlanningCoverage;
    // delivered sales (status Entregado) cover the full requested amount:
    // restante shows 0 and the line consumes no inventory
    isComplete: boolean;
    // False for pellet/lavado, whose consumption is never captured so
    // getProductsInventory returns no row. The card must then read "sin dato de
    // inventario" — a neutral chip — instead of "Inv: 0" with a red dot, because
    // nothing is known to be missing. Shared helper keyed on the production type.
    inventoryTracked: boolean;
};

export type PlanningOrder = {
    order_request_id: number;
    order_code: number | null;
    account_name: string;
    order_request_status_id: number;
    order_request_status_name: string;
    order_request_date: string | null;
    order_request_estimated_delivery_date: string | null;
    priority: number;
    items: PlanningItem[];
};

export function OrdersToFulfill() {
    const { data, loading, refetch } = useOrdersToFulfillPageQuery();
    const [updatePriorities] = useUpdateOrderRequestPrioritiesMutation();
    const [isSaving, setIsSaving] = useState(false);

    // Reordering writes the priority column, which the backend gates to
    // Produccion (main). Hide the controls for everyone else — Ventas and both
    // assistants included — so the board stays view-only for them.
    const { data: currentUserData } = useCurrentUserQuery();
    const canReorder =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin, RoleId.Production].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );

    // Status change is admin-only (Super / General), matching the backend
    // updateOrderRequestStatus gate — a narrower set than reordering.
    const canChangeStatus =
        !!currentUserData &&
        [RoleId.Super, RoleId.Admin].some((roleId) =>
            currentUserData.currentUser.role_ids.includes(roleId),
        );

    // One status dialog for the whole board; a card double-click fills these.
    const [statusOpen, setStatusOpen] = useState(false);
    const [statusOrderRequestId, setStatusOrderRequestId] = useState<
        number | null
    >(null);
    const [statusCurrentId, setStatusCurrentId] = useState<number | null>(null);

    const handleChangeStatus = (order: PlanningOrder) => {
        setStatusOrderRequestId(order.order_request_id);
        setStatusCurrentId(order.order_request_status_id);
        setStatusOpen(true);
    };

    // Refetch on close so a status change re-buckets or removes the card. The
    // dialog exposes no success signal, so a no-op refetch on cancel is fine.
    const handleStatusDialogOpen = (open: boolean) => {
        setStatusOpen(open);
        if (!open) void refetch();
    };

    const planningOrders = useMemo<PlanningOrder[]>(() => {
        if (!data) return [];

        const rowsByOrder = new Map<number, RequestProductRow[]>();
        data.getOptimizedRequestProducts.forEach((row) => {
            if (!row.order_request_id) return;
            const rows = rowsByOrder.get(row.order_request_id) || [];
            rows.push(row);
            rowsByOrder.set(row.order_request_id, rows);
        });

        // "En producción" (status 2) tickets first, then "Pendiente" (status 1).
        // Within each status: manual priority ascending (lower = higher on the
        // board), then oldest request first (kitchen FIFO). Priority starts at 0
        // for every request, so untouched buckets fall back to date order.
        const sortedOrders = Array.from(rowsByOrder.values()).sort((a, b) => {
            const statusA = a[0].order_request_status_id || 0;
            const statusB = b[0].order_request_status_id || 0;
            if (statusA !== statusB) return statusB - statusA;
            const priorityA = a[0].priority || 0;
            const priorityB = b[0].priority || 0;
            if (priorityA !== priorityB) return priorityA - priorityB;
            const dateA = a[0].order_request_date
                ? new Date(a[0].order_request_date).getTime()
                : Number.MAX_SAFE_INTEGER;
            const dateB = b[0].order_request_date
                ? new Date(b[0].order_request_date).getTime()
                : Number.MAX_SAFE_INTEGER;
            if (dateA !== dateB) return dateA - dateB;
            return (a[0].order_code || 0) - (b[0].order_code || 0);
        });

        // Committed stock a VISIBLE line will still consume through its own
        // `remaining`. A pedido keeps owing its full remaining until delivery,
        // so this portion has to stay in the pool — taking it out here as well
        // would charge the same bultos twice: once to the pedido that already
        // has the sale, and again to everyone behind it.
        //
        // Capped at the line's remaining, because committed beyond what a line
        // can still take is stock nothing on this board will draw down.
        const claimedCommitted = new Map<
            number,
            { kilos: number; groups: number }
        >();
        data.getOptimizedRequestProducts.forEach((row) => {
            if (!row.product_id) return;
            const claimed = claimedCommitted.get(row.product_id) || {
                kilos: 0,
                groups: 0,
            };
            claimed.kilos += Math.min(
                Math.max(0, row.order_sale_committed_kilos || 0),
                Math.max(0, row.order_sale_remaining_kilos || 0),
            );
            claimed.groups += Math.min(
                Math.max(0, row.order_sale_committed_groups || 0),
                Math.max(0, row.order_sale_remaining_groups || 0),
            );
            claimedCommitted.set(row.product_id, claimed);
        });

        // Mutable stock pool: tickets consume it in queue order, so a later
        // ticket only sees what earlier tickets left over.
        //
        // Seeded with the RAW balance rather than a clamped one. A negative
        // balance is a debt that has to stay visible all the way down the queue;
        // clamping it to 0 is what made a product in deficit read as "0" and
        // prompted the raw-inventory display that broke the cascade.
        const stockPool = new Map<number, { kilos: number; groups: number }>();
        const rawInventory = new Map<number, { kilos: number; groups: number }>();
        const looseCommitted = new Map<
            number,
            { kilos: number; groups: number }
        >();
        data.getProductsInventory.forEach((inventory) => {
            if (!inventory.product_id) return;
            const claimed = claimedCommitted.get(inventory.product_id) || {
                kilos: 0,
                groups: 0,
            };
            // Whatever is committed but unclaimed by the board is really gone.
            const loose = {
                kilos: Math.max(
                    0,
                    (inventory.kilos_committed || 0) - claimed.kilos,
                ),
                groups: Math.max(
                    0,
                    (inventory.groups_committed || 0) - claimed.groups,
                ),
            };
            rawInventory.set(inventory.product_id, {
                kilos: inventory.kilos ?? 0,
                groups: inventory.groups ?? 0,
            });
            looseCommitted.set(inventory.product_id, loose);
            stockPool.set(inventory.product_id, {
                kilos: (inventory.kilos ?? 0) - loose.kilos,
                groups: (inventory.groups ?? 0) - loose.groups,
            });
        });

        const orders: PlanningOrder[] = [];
        sortedOrders.forEach((rows) => {
            const items: PlanningItem[] = [];
            rows.forEach((row) => {
                const useGroups = (row.order_request_groups || 0) > 0;
                // remaining = requested - delivered (sales in status Entregado,
                // subtracted by the SQL). Clamp at 0 so over-delivery also
                // reads "0 restante" instead of a negative number.
                const rawRemaining = useGroups
                    ? row.order_sale_remaining_groups || 0
                    : row.order_sale_remaining_kilos || 0;
                const remaining = Math.max(0, rawRemaining);
                const isComplete = remaining === 0;

                const stock = row.product_id
                    ? stockPool.get(row.product_id)
                    : undefined;
                const inventory = row.product_id
                    ? rawInventory.get(row.product_id)
                    : undefined;
                const loose = row.product_id
                    ? looseCommitted.get(row.product_id)
                    : undefined;
                const available = stock
                    ? useGroups
                        ? stock.groups
                        : stock.kilos
                    : 0;
                // a completed line demands nothing, so it consumes no stock.
                // Allocation reads the pool through a 0 floor so a debt covers
                // nothing, while `available` itself stays negative for display.
                const covered = Math.min(remaining, Math.max(0, available));
                if (stock && covered > 0) {
                    if (useGroups) {
                        stock.groups = available - covered;
                    } else {
                        stock.kilos = available - covered;
                    }
                }

                items.push({
                    product_id: row.product_id || null,
                    product_description: row.product_description || '',
                    unit: useGroups ? 'bultos' : 'kg',
                    requested: useGroups
                        ? row.order_request_groups || 0
                        : row.order_request_kilos || 0,
                    inventory: inventory
                        ? useGroups
                            ? inventory.groups
                            : inventory.kilos
                        : 0,
                    committed: loose
                        ? useGroups
                            ? loose.groups
                            : loose.kilos
                        : 0,
                    remaining,
                    covered,
                    available,
                    coverage:
                        covered >= remaining
                            ? 'full'
                            : covered > 0
                            ? 'partial'
                            : 'none',
                    isComplete,
                    inventoryTracked: isInventoryTracked(
                        row.order_production_type_id ?? null,
                    ),
                });
            });

            const first = rows[0];
            orders.push({
                order_request_id: first.order_request_id as number,
                order_code: first.order_code || null,
                account_name: first.account_name,
                order_request_status_id: first.order_request_status_id || 0,
                order_request_status_name: first.order_request_status_name,
                order_request_date: first.order_request_date || null,
                order_request_estimated_delivery_date:
                    first.order_request_estimated_delivery_date || null,
                priority: first.priority || 0,
                items,
            });
        });

        return orders;
    }, [data]);

    // Move a card within its own status bucket. Because every request starts at
    // priority 0, plain midpoint math can't separate tied cards — so we reorder
    // the visible bucket in memory, renumber it 0..N-1, and persist only the
    // cards whose priority actually changed (the first move in a bucket seeds it;
    // later moves touch ~2). A refetch then re-sorts from the saved truth.
    const handleMove = async (order: PlanningOrder, move: PlanningMove) => {
        if (isSaving) return;

        const bucket = planningOrders.filter(
            (o) => o.order_request_status_id === order.order_request_status_id,
        );
        const currentIndex = bucket.findIndex(
            (o) => o.order_request_id === order.order_request_id,
        );
        if (currentIndex === -1) return;

        const newIndex =
            move === 'top'
                ? 0
                : move === 'up'
                ? currentIndex - 1
                : currentIndex + 1;
        if (newIndex < 0 || newIndex >= bucket.length || newIndex === currentIndex) {
            return;
        }

        const reordered = [...bucket];
        const [moved] = reordered.splice(currentIndex, 1);
        reordered.splice(newIndex, 0, moved);

        const updates = reordered
            .map((o, index) => ({ order_request_id: o.order_request_id, priority: index }))
            .filter((u, index) => (reordered[index].priority || 0) !== u.priority);

        if (updates.length === 0) return;

        setIsSaving(true);
        try {
            await updatePriorities({
                variables: {
                    OrderRequestPrioritiesInput: updates,
                },
            });
            await refetch();
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <DPage>
            <DPageBody disableGutters>
                {loading ? (
                    <DPageLoader />
                ) : (
                    <Box
                        sx={{
                            px: 2,
                            pb: 2,
                            flex: 1,
                            minHeight: 0,
                            overflow: 'auto',
                        }}
                    >
                        <CoverageLegend />
                        {planningOrders.length === 0 ? (
                            <Typography color={'text.secondary'}>
                                No hay pedidos pendientes por producir
                            </Typography>
                        ) : (
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        'repeat(auto-fill, minmax(320px, 1fr))',
                                    gap: 2,
                                }}
                            >
                                {planningOrders.map((order, index) => {
                                    // buckets are contiguous in the sorted list,
                                    // so a status change marks a bucket edge
                                    const prev = planningOrders[index - 1];
                                    const next = planningOrders[index + 1];
                                    const isFirstInBucket =
                                        !prev ||
                                        prev.order_request_status_id !==
                                            order.order_request_status_id;
                                    const isLastInBucket =
                                        !next ||
                                        next.order_request_status_id !==
                                            order.order_request_status_id;
                                    return (
                                        <PlanningOrderCard
                                            key={order.order_request_id}
                                            order={order}
                                            canReorder={canReorder}
                                            isSaving={isSaving}
                                            isFirstInBucket={isFirstInBucket}
                                            isLastInBucket={isLastInBucket}
                                            onMove={handleMove}
                                            canChangeStatus={canChangeStatus}
                                            onChangeStatus={handleChangeStatus}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                )}
            </DPageBody>
            <SUpdateOrderRequestStatusDialog
                open={statusOpen}
                setOpen={handleStatusDialogOpen}
                orderRequestId={statusOrderRequestId}
                currentStatusId={statusCurrentId}
            />
        </DPage>
    );
}
